import { axios, axiosWithoutHeaders } from "./axiosWrapper";
import compareVersions, { VERSION_SAFARI_ENCODING_FOR_PRESIGN_AND_UPLOAD } from "@cores/version";
import { withRetryNetworkRequest } from "@utils/withRetryNetworkRequest";
import { createPreviewResourcePresignUrl } from "@modules/asset";

import retryAsync from "@cores/retryInvoker";

const MINIMUM_CHUNK_SIZE = 1024 * 1000 * 40; // 40MB
const MAXIMUM_SINGLE_FILE_UPLOAD_SIZE = 1024 * 1000 * 1000; // 1 GB
const MAXIMUM_MULTIPART_COUNT = 10000; // AWS S3 Multipart 파트 개수는 1만개가 최대이다.

export const RESOURCE_TYPES = {
    SOURCES: "SOURCES",
}; //Note:dd

// ###########
// # CAUTION #
// ##########
// 1.0.14 버전 이상의 Space만 사용할 수 있음.
// 호출하는 곳에서 분기 처리 필요함.
const presignUrl = async (
    apiEndpoint,
    stageVersion,
    { resourceType, resourceId, threadId, bucket, prefix, file, projectId, requestId },
) => {
    let requestBody = {
        resourceType,
        resourceId,
        threadId,
        requestId,
        bucket,
        prefix,
        fileName: file.name,
        contentType: file.type?.length > 0 ? file.type : tryGetMimeType(file),
    };
    let headers = {};
    if (projectId) headers.projectId = projectId;
    if (compareVersions(stageVersion, VERSION_SAFARI_ENCODING_FOR_PRESIGN_AND_UPLOAD) >= 0) {
        requestBody = Buffer.from(encodeURIComponent(JSON.stringify(requestBody))).toString("base64");
        headers = {
            ...headers,
            "x-mzc-content-encoding": "base64",
        };
    }
    const response = await retryAsync(async () => {
        return await axios.post(`${apiEndpoint}/aws/s3/uploads/presigned-url`, requestBody, {
            headers,
        });
    }, (e) => {
        if(e?.response?.status === 403) {
            throw e;
        }
        return false;
    });
    return response.data;
};

const createMultipartUpload = async (
    apiEndpoint,
    stageVersion,
    { resourceType, resourceId, threadId, projectId, bucket, prefix, file, partSize, localFilePath, fileSize },
) => {
    let requestBody = {
        resourceType,
        resourceId,
        threadId,
        bucket,
        prefix,
        fileName: file.name,
        contentType: file.type?.length > 0 ? file.type : tryGetMimeType(file),
        partSize,
        localFilePath,
        fileSize,
    };
    let headers = {};
    if (projectId) headers.projectId = projectId;

    if (compareVersions(stageVersion, VERSION_SAFARI_ENCODING_FOR_PRESIGN_AND_UPLOAD) >= 0) {
        requestBody = Buffer.from(encodeURIComponent(JSON.stringify(requestBody))).toString("base64");
        headers = {
            ...headers,
            "x-mzc-content-encoding": "base64",
        };
    }
    const response =  await retryAsync(async () => {
        return await axios.post(`${apiEndpoint}/aws/s3/uploads/multipart`, requestBody, {
            headers,
        });
    }, (e) => {
        if(e?.response?.status === 403) {
            throw e;
        }
        return false;
    });
    return response.data;
};

const presignUploadPart = async (
    apiEndpoint,
    stageVersion,
    projectId,
    { bucket, key, uploadId, partNumber, cancelToken },
) => {
    let requestBody = {
        bucket,
        key,
        partNumber,
    };
    let headers = {};
    if (projectId) headers.projectId = projectId;

    if (compareVersions(stageVersion, VERSION_SAFARI_ENCODING_FOR_PRESIGN_AND_UPLOAD) >= 0) {
        requestBody = Buffer.from(encodeURIComponent(JSON.stringify(requestBody))).toString("base64");
        headers = {
            ...headers,
            "x-mzc-content-encoding": "base64",
        };
    }
    const response =  await retryAsync(async () => {
        return await withRetryNetworkRequest(() =>
            axios.post(`${apiEndpoint}/aws/s3/uploads/multipart/${uploadId}/parts`, requestBody, {
                headers,
                cancelToken,
            })
        );
    }, (e) => {
        if(e?.response?.status === 403) {
            throw e;
        }
        return false;
    });
    return response.data;
};

const completeMultipartUpload = async (apiEndpoint, stageVersion, projectId, { bucket, key, uploadId, parts }) => {
    let requestBody = {
        bucket,
        key,
        parts,
    };
    let headers = {};
    if (projectId) headers.projectId = projectId;

    if (compareVersions(stageVersion, VERSION_SAFARI_ENCODING_FOR_PRESIGN_AND_UPLOAD) >= 0) {
        requestBody = Buffer.from(encodeURIComponent(JSON.stringify(requestBody))).toString("base64");
        headers = {
            ...headers,
            "x-mzc-content-encoding": "base64",
        };
    }
    const response =  await retryAsync(async () => {
        return await axios.post(`${apiEndpoint}/aws/s3/uploads/multipart/${uploadId}`, requestBody, {
            headers,
        });
    }, (e) => {
        if(e?.response?.status === 403) {
            throw e;
        }
        return false;
    });
    return response.data;
};

const abortMultipartUpload = async (apiEndpoint, stageVersion, projectId, { bucket, key, uploadId }) => {
    let requestBody = {
        bucket,
        key,
        uploadId,
    };
    let headers = {};
    if (projectId) headers.projectId = projectId;

    if (compareVersions(stageVersion, VERSION_SAFARI_ENCODING_FOR_PRESIGN_AND_UPLOAD) >= 0) {
        requestBody = Buffer.from(encodeURIComponent(JSON.stringify(requestBody))).toString("base64");
        headers = {
            ...headers,
            "x-mzc-content-encoding": "base64",
        };
    }
    const response =  await retryAsync(async () => {
        return  await axios.delete(`${apiEndpoint}/aws/s3/uploads/multipart/${uploadId}`, {
            data: requestBody,
            headers,
        });
    }, (e) => {
        if(e?.response?.status === 403) {
            throw e;
        }
        return false;
    });
    return response.data;
};

const uploadMultipart = async (url, file, contentType, index, cancelToken, uploadProgress) => {
    return await retryAsync(async () => {
        return await axiosWithoutHeaders.put(url, file, {
            headers: { "Content-Type": contentType || "application/octet-stream" },
            onUploadProgress: (event) => {
                uploadProgress(event, index);
            },
            customErrorMessage: true,
            cancelToken,
        });
    }, (e) => {
        if(e?.response?.status === 403) {
            throw e;
        }
        return false;
    });
};
const tryGetMimeType = (file) => {
    const { name } = file;
    if (name.endsWith(".exr")) {
        return "image/x-exr";
    }
    if (name.endsWith(".tga")) {
        return "image/x-tga";
    }
    return "application/octet-stream";
};

const getPresignedUrlByType = async (apiEndpoint, stageVersion, type, file, presignedUrlParams = {}, dispatch) => {
    await retryAsync(async () => {
        switch (type) {
            case "Preview": {
                const response = await dispatch(createPreviewResourcePresignUrl(presignedUrlParams));
                file.uploadUrl = response?.data?.uploadUrl;
                break;
            }
            case "Source":
            case "Asset":
            case "CommentAttachment":
            case "REQUEST_ATTACHMENT":
            case "Video": {
                const response = await withRetryNetworkRequest(() =>
                    presignUrl(apiEndpoint, stageVersion, {
                        file,
                        ...presignedUrlParams,
                    }),
                );
                file.uploadUrl = response?.uploadUrl;
                file.url = response?.url;
                break;
            }
            default: {
                // ignored
            }
        }
    }, (e) => {
        if(e?.response?.status === 403) {
            throw e;
        }
        return false;
    });
};

/**
 * 일반 업로드. 지정된 URL로 파일을 PUT 한다.
 */
const runUpload = async (params) => {
    const {
        file,
        cancelToken,
        onUploadProgress = () => {},
        onComplete = () => {},
        onCancel = () => {},
        onError = (err) => {},
        dispatch,
        params: { type, apiEndpoint, stageVersion, uploadParams },
    } = params;

    try {
        await getPresignedUrlByType(apiEndpoint, stageVersion, type, file, uploadParams, dispatch);
        const presignedUrl = file.uploadUrl;
        await retryAsync(async () => {
            await withRetryNetworkRequest(() =>
                axiosWithoutHeaders.put(presignedUrl, file, {
                    headers: {
                        "Content-Type": file.type?.length > 0 ? file.type : tryGetMimeType(file),
                    },
                    customErrorMessage: true,
                    onUploadProgress,
                    cancelToken,
                }),
            );
        }, (e) => {
            if(e?.response?.status === 403) {
                throw e;
            }
            return false;
        });
        onComplete({
            destinationUrl: presignedUrl.substring(0, presignedUrl.indexOf("?")),
        });
    } catch (e) {
        console.log(e);
        if(e?.response?.status === 403) {
            onError(e);
        } else if (["ERR_NETWORK", "ECONNRESET", "EPIPE"].includes(e?.code)) {
            return await runUpload(params);
        } else if (axios.isCancel(e)) {
            onCancel();
        } else {
            onError(e);
        }
    }
};

const uploadPart = async (apiEndpoint, stageVersion, projectId, part) => {
    try {
        const { bucket, key, uploadId, partNumber, blob, cancelToken, partUploadProgress, contentType } = part;

        const presignResponse = await retryAsync(async () => {
            return await presignUploadPart(apiEndpoint, stageVersion, projectId, {
                bucket,
                key,
                uploadId,
                partNumber,
                cancelToken,
            });
        }, (e) => {
            if(e?.response?.status === 403) {
                throw e;
            }
            return false;
        });

        const uploadResponse = await retryAsync(async () => {
            const response = await withRetryNetworkRequest(() =>
                uploadMultipart(presignResponse.uploadUrl, blob, contentType, partNumber, cancelToken, partUploadProgress),
            );
            if(!response?.headers?.etag) {
                throw Error(`no ETAG in response headers`);
            }
            return response;
        }, (e) => {
            if(e?.response?.status === 403) {
                throw e;
            }
            return false;
        });
        return {
            ETag: uploadResponse.headers.etag,
            PartNumber: partNumber,
        };
    } catch (error) {
        if(error?.response?.status === 403) {
            throw error;
        } else if (["ERR_NETWORK", "ECONNRESET", "EPIPE"].includes(error?.code)) {
            return await uploadPart(apiEndpoint, stageVersion, projectId, part);
        } else {
            throw error;
        }
    }
};

const uploadParts = async (apiEndpoint, stageVersion, projectId, count, uploadItems) => {
    try {
        const parts = uploadItems.splice(0, count);
        let copiedParts = [...parts];
        let uploadedFiles = [];

        do {
            const uploadFiles = await Promise.allSettled(
                copiedParts.map(async (part, index) => {
                    try {
                        return await uploadPart(apiEndpoint, stageVersion, projectId, part);
                    } catch (error) {
                        return Promise.reject(error);
                    }
                }),
            );

            copiedParts = [];
            uploadFiles.forEach((uploadPromise, i) => {
                if (uploadPromise.status === "rejected") {
                    if (axios.isCancel(uploadPromise.reason)) {
                        throw uploadPromise.reason;
                    } else copiedParts.push(parts[i]);
                } else {
                    uploadedFiles.push(uploadPromise.value);
                }
            });
        } while (copiedParts.length > 0);

        if (uploadItems.length === 0) {
            return uploadedFiles;
        } else {
            const restList = await uploadParts(apiEndpoint, stageVersion, projectId, count, uploadItems);
            return uploadedFiles.concat(restList);
        }
    } catch (error) {
        console.error(error);
        throw error;
    }
};

const runMultipartUpload = async ({
    params: {
        stageVersion,
        uploadId,
        uploadParams: { resourceType, resourceId, threadId, projectId, bucket, prefix },
        apiEndpoint,
    },
    file,
    cancelToken,
    onUploadProgress = () => {},
    onComplete = () => {},
    onCancel = () => {},
    onError = (err) => {},
}) => {
    let key = "";
    try {
        if (prefix && prefix.length > 0) {
            key = `${prefix}/${file.name}`;
        } else {
            key = file.name;
        }
        const fileSize = file.size;
        let chunkSize = fileSize / MAXIMUM_MULTIPART_COUNT;
        if (chunkSize < MINIMUM_CHUNK_SIZE) {
            chunkSize = MINIMUM_CHUNK_SIZE;
        }

        if (!uploadId) {
            const createMultipartUploadResponse = await retryAsync(async () => {
                return await withRetryNetworkRequest(() =>
                    createMultipartUpload(apiEndpoint, stageVersion, {
                        resourceType,
                        resourceId,
                        threadId,
                        projectId,
                        file,
                        bucket,
                        prefix,
                        partSize: chunkSize,
                        localFilePath: file.id,
                        fileSize: file.size,
                    }),
                );
            }, (e) => {
                if(e?.response?.status === 403) {
                    throw e;
                }
                return false;
            });
            uploadId = createMultipartUploadResponse.uploadId;
            bucket = createMultipartUploadResponse.bucket;
            key = createMultipartUploadResponse.key;
            file.uploadId = uploadId;
        }

        const chunkCount = Math.floor(fileSize / chunkSize) + 1;
        let total = [];
        const partUploadProgress = async (event, index) => {
            total[index] = event.loaded;
            const totalLoaded = total.reduce((a, c) => a + c);
            const newEvent = {
                loaded: totalLoaded,
                total: fileSize,
            };
            onUploadProgress(newEvent);
        };

        const uploadItems = [];
        for (let i = 1; i <= chunkCount; i++) {
            let start = (i - 1) * chunkSize;
            let end = i * chunkSize;
            let blob = i <= chunkCount ? file.slice(start, end) : file.slice(start);
            uploadItems.push({
                bucket,
                key,
                uploadId,
                partNumber: i,
                blob,
                cancelToken,
                partUploadProgress,
                contentType: file.type?.length > 0 ? file.type : tryGetMimeType(file),
            });
        }
        const uploadedParts = await uploadParts(apiEndpoint, stageVersion, projectId, 2, uploadItems); // 2개씩 끊어서 업로드
        if (uploadedParts.length !== chunkCount) {
            throw new Error(`uploaded parts count(${uploadedParts.length}) does not match chunkCount(${chunkCount})`);
        }
        const completeResponse =  await retryAsync(async () => {
            return await withRetryNetworkRequest(() =>
                completeMultipartUpload(apiEndpoint, stageVersion, projectId, {
                    bucket,
                    key,
                    uploadId,
                    parts: uploadedParts.sort(function (a, b) {
                        return a.PartNumber - b.PartNumber;
                    }),
                }),
            );
        }, (e) => {
            if(e?.response?.status === 403) {
                throw e;
            }
            return false;
        });
        onComplete({
            destinationUrl: completeResponse.destinationUrl,
        });
    } catch (error) {
        if (uploadId) {
            try {
                await retryAsync(async () => {
                    return await abortMultipartUpload(apiEndpoint, stageVersion, projectId, {
                        bucket,
                        key,
                        uploadId,
                    });
                }, (e) => {
                    if(e?.response?.status === 403) {
                        throw e;
                    }
                    return false;
                });
            } catch (innerException) {
                // ignored
            }
        }
        if (axios.isCancel(error)) {
            onCancel();
        } else {
            onError(error);
        }
    }
};

//맨 처음 RightSidebar 마운트 시 멀티파트 안된 잡 조회 -> 있으면 각각 abort => rightsidebar
//online 이벤트 연결 -> rightsidebar, network error에 대한 처리 -> create 이후 작업들에 추가

//polling 저장시 파일데이터, 파트사이즈, 사이즈, 이름 저장해두기(혹시 모르니)

/**
 * @param {string} uploadId: 다른 곳에서 Multipart upload ID를 획득해놓은 경우 기입
 * @param {string} presignedUrl: 다른 곳에서 Presigned URL을 획득해놓은 경우 기입
 * @param {string} resourceType: SOURCE - 추후 점진적으로 ASSET | JOB | RESOURCE 등으로 늘려가서 업로드는 하나의 API만 보게 할 것임.
 * @param {string} resourceId: 특정 리소스를 타겟팅하는 경우 ID. ex) Asset ID
 * @param {string} bucket: 버킷 이름
 * @param {string} prefix: 파일 이름 앞에 붙혀질 폴더 경로
 */
export const upload = async (
    file,
    params,
    cancelToken,
    onUploadProgress = () => {},
    onComplete = () => {},
    onCancel = () => {},
    onError = () => {},
    dispatch,
) => {
    const newParams = {
        file,
        params,
        cancelToken,
        onUploadProgress,
        onComplete,
        onCancel,
        onError,
        dispatch,
    };

    if (file?.size > MAXIMUM_SINGLE_FILE_UPLOAD_SIZE) {
        await runMultipartUpload(newParams);
    } else {
        await runUpload(newParams);
    }
};

export default upload;
